// src/router/index.tsx
import React, { useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate, useParams, Outlet, Navigate } from 'react-router-dom';

import LandingPage from '../pages/LandingPage';
import CorpLanding from '../pages/CorpLanding';
import LiffEntry from '../pages/LiffEntry';
import MagicPayment from '../pages/MagicPayment';
import ConsultationPage from '../pages/ConsultationPage';
import ReservationPage from '../pages/Reservation';
import MyPage from '../pages/MyPage';
import Welcome from '../pages/Welcome';
import MyReservations from '../pages/MyReservations';
import MypageRecords from '../pages/MypageRecords';
import MypageExtra from '../pages/MypageExtra';
import MypagePoints from '../pages/MypagePoints';

import MypageRegular from '../pages/MypageRegular';
import MypageMessages from '../pages/MypageMessages';
import MypageShell from '../components/MypageShell';
import About from '../pages/About';

import AgaMalePage from '../pages/menu/AgaMale';
import AgaFemalePage from '../pages/menu/AgaFemale';
import ObesityMalePage from '../pages/menu/ObesityMale';
import ObesityFemalePage from '../pages/menu/ObesityFemale';
import EdPrematurePage from '../pages/menu/EdPremature';
import PillPage from '../pages/menu/Pill';
import PillLowDosePage from '../pages/menu/PillLowDosePage';
import PillPeriodShiftPage from '../pages/menu/PillPeriodShiftPage';
import AfterPillPage from '../pages/menu/AfterPillPage';
import MedicalSkincarePage from '../pages/menu/MedicalSkincare';
import FaqMedicalDietFemale from '../pages/faq/FaqMedicalDietFemale';
import FaqMedicalDietMale from '../pages/faq/FaqMedicalDietMale';
import FaqAgaMale from '../pages/faq/FaqAgaMale';
import FaqAgaFemale from '../pages/faq/FaqAgaFemale';
import FaqEdPremature from '../pages/faq/FaqED';
import FaqPill from '../pages/faq/FaqPillLowDose';
import FaqMedicalSkincare from '../pages/faq/FaqMedicalSkincare';

import QuestionnaireAGAMale from '../pages/QuestionnaireAGAMale';
import QuestionnaireAGAFemale from '../pages/QuestionnaireAGAFemale';
import QuestionnaireEDPremature from '../pages/QuestionnaireEDPremature';
import QuestionnaireObesityFemale from '../pages/QuestionnaireObesityFemale';
import QuestionnaireObesityMale from '../pages/QuestionnaireObesityMale';
import QuestionnairePill from '../pages/QuestionnairePill';
import QuestionnaireMedicalSkincare from '../pages/QuestionnaireMedicalSkincare';

import UploadPrescription from '../pages/doctor/UploadPrescription';
import DoctorQuestionnaire from '../pages/doctor/DoctorQuestionnaire';
import DoctorDashboard from '../pages/doctor/DoctorDashboard';
import DoctorRecord from '../pages/doctor/DoctorRecord';
import DoctorConsultation from '../pages/doctor/DoctorConsultation';
import DoctorUsers from '../pages/doctor/DoctorUsers';
import DoctorLogin from '../pages/doctor/DoctorLogin';
import DoctorSchedule from '../pages/doctor/DoctorSchedule';
import DoctorReservations from '../pages/doctor/DoctorReservations';
import DoctorMyPage from '../pages/doctor/DoctorMyPage';

import AdminLogin from '../pages/admin/AdminLogin';
import AdminLayout from '../components/layouts/AdminLayout';
import AdminDashboard from '../pages/admin/AdminDashboard';
import AdminMedicine from '../pages/admin/AdminMedicine';
import AdminMedicineList from '../pages/admin/AdminMedicineList';
import AdminReservations from '../pages/admin/AdminReservations';
import AdminIPCheck from '../pages/admin/AdminIPCheck';
import AdminDoctorList from '../pages/admin/AdminDoctorList';
import AdminUserList from '../pages/admin/AdminUserList';
import AdminSchedules from '../pages/admin/AdminSchedules';
import AdminDoctorView from '../pages/admin/AdminDoctorView';
import AdminCouponList from '../pages/admin/coupons/AdminCouponList';
import AdminCouponCreate from '../pages/admin/coupons/AdminCouponCreate';
import AdminCouponDetail from '../pages/admin/coupons/AdminCouponDetail';
import AdminReferralsPage from '../pages/admin/AdminReferralsPage';
import AdminCompanyList from '../pages/admin/AdminCompanyList';
import AdminPointsManage from '../pages/admin/AdminPointsManage';
import AdminAutoAssignPage from '../pages/admin/AdminAutoAssignPage';
import AdminSenderAddresses from '../pages/admin/AdminSenderAddresses';
import AdminDeliveryManagement from '../pages/admin/AdminDeliveryManagement';
import AdminAdditionalSupply from '../pages/admin/AdminAdditionalSupply';
import AdminUserMerge from '../pages/admin/AdminUserMerge';
import AdminZoomManagement from '../pages/admin/AdminZoomManagement';
import AdminNotificationSettings from '../pages/admin/AdminNotificationSettings';


import ConsultationRoom from '../pages/ConsultationRoom';
import UserProfilePage from '../pages/UserProfileForm';
import ContactPage from '../pages/ContactPage';
import FAQPage, { FaqPickupModal } from '../pages/FAQPage';
import PrivacyPolicyPage from '../pages/PrivacyPolicyPage';
import SecurityPolicyPage from '../pages/SecurityPolicyPage';
import LegalPage from '../pages/LegalPage';
import TermsOfUsePage from '../pages/TermsOfUsePage';
import LoginHandler from '../pages/LoginHandler';
import ResetPassword from '../pages/ResetPassword';
import Register from '../pages/Register';
import OAuthCallback from '../pages/OAuthCallback';
import Verify from '../pages/Verify';
import VerifyPending from '../pages/VerifyPending';

import MainLayout from '../components/layouts/MainLayout';
import DoctorLayout from '../components/layouts/DoctorLayout';
import ProtectedRoute from './ProtectedRoute';
import NotFound from '../pages/NotFound';

// /invite?ref=xxx → /?ref=xxx へリダイレクト（LP の招待モーダルを使う）
const InviteRedirect: React.FC = () => {
  const { search } = useLocation();
  return <Navigate to={`/${search}`} replace />;
};

// /c/:linkCode → バックエンドの短縮リンクハンドラへパススルー
// - 本番: nginx が /api/* を FastAPI に転送するため /api/c/:linkCode へリダイレクト
// - 開発: CRA の proxy は navigation リクエスト(text/html)をプロキシしないため
//         直接 localhost:8000/c/:linkCode へリダイレクト
// useParams() で linkCode を取り出すことで pathname 連結によるパストラバーサルを防ぐ
const CouponLinkPassthrough: React.FC = () => {
  const { linkCode } = useParams<{ linkCode: string }>();
  useEffect(() => {
    if (!linkCode) return;
    // 英数字 + ハイフン / アンダーバーのみ許可（それ以外はバックエンドが 404 を返す）
    const safe = encodeURIComponent(linkCode);
    const isLocalDev =
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1';
    if (isLocalDev) {
      window.location.replace(`http://localhost:8000/c/${safe}`);
    } else {
      window.location.replace(`/api/c/${safe}`);
    }
  }, [linkCode]);
  return null;
};

interface AppRoutesProps {
  ipAllowed: boolean;
}


const ExternalRedirect: React.FC<{ to: string }> = ({ to }) => {
  useEffect(() => {
    // Only redirect on real deployed environments, never on localhost
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return;
    }
    try {
      const targetUrl = new URL(to);
      if (window.location.hostname !== targetUrl.hostname) {
        // PRESERVE QUERY PARAMS: Append existing search params to the new URL
        const finalUrl = targetUrl.origin + targetUrl.pathname + window.location.search;
        window.location.href = finalUrl;
      }
    } catch (e) {
      console.error("Invalid URL in ExternalRedirect:", to);
    }
  }, [to]);

  return null;
};

/**
 * WeChat Authorization Callback Handler (Frontend Bridge)
 * Redirects /wechat/callback?code=... to /api/wechat/callback?code=...
 */
const WeChatCallbackHandler: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    // Forward the current query parameters to the backend
    window.location.href = `/api/wechat/callback${location.search}`;
  }, [location.search]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-white">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-coral border-t-transparent rounded-full animate-spin" />
        <p className="text-slate-500 font-medium animate-pulse">微信登录验证中...</p>
      </div>
    </div>
  );
};

const AppRoutes: React.FC<AppRoutesProps> = ({ ipAllowed }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Move useEffect BEFORE any early return to comply with React hooks rules
  useEffect(() => {
    const role = localStorage.getItem('role');
    // ユーザー専用パス（doctor/admin がアクセスすべきでないパス）
    const userOnlyPaths = ['/mypage', '/reserve', '/welcome', '/my/', '/payment/'];
    const isUserOnlyPath =
      location.pathname === '/' ||
      userOnlyPaths.some(p => location.pathname.startsWith(p));
    if (role === 'doctor' && isUserOnlyPath) {
      navigate('/doctor/dashboard', { replace: true });
    }
    if (role === 'admin' && isUserOnlyPath) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [location, navigate]);

  // 法人専用リンク(/corp/:code)経由でログインした利用者を、ログイン直後の既定着地
  // (/mypage・/welcome)からのみ社員番号入力へ復帰させる。他ページへ移動したら引き戻さない
  // (ピン留め防止)。妥当なコードのみ・1時間で期限切れ。
  useEffect(() => {
    const corpCode = sessionStorage.getItem('pending_corp_code');
    if (!corpCode || !/^[A-Z0-9_-]+$/.test(corpCode)) return;
    const at = Number(sessionStorage.getItem('pending_corp_code_at') || 0);
    if (at && Date.now() - at > 3600000) {
      sessionStorage.removeItem('pending_corp_code');
      sessionStorage.removeItem('pending_corp_code_at');
      return;
    }
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    if (token && role === 'user' && ['/mypage', '/welcome'].includes(location.pathname)) {
      navigate(`/corp/${corpCode}`, { replace: true });
    }
  }, [location, navigate]);

  // If the user accesses via lp.smhc.ai OR localhost, intercept root/auth paths
  // and render LandingPage OUTSIDE of MainLayout (no navbar/footer).
  const isLpDomain = window.location.hostname === 'lp.smhc.ai';
  const isTestDomain = window.location.hostname.includes('test.smhc.ai');
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const interceptedPaths = ['/', '/signup', '/forgot', '/wechat/callback', '/wechat/login'];
  
  if ((isLpDomain || isLocalhost || isTestDomain) && (interceptedPaths.includes(location.pathname) || location.pathname === '/login_success')) {
    // Specialized handlers for WeChat callback or successful login
    if (location.pathname === '/wechat/callback' || location.pathname === '/wechat/login') {
      return <WeChatCallbackHandler />;
    }
    if (location.pathname === '/login_success') {
      return <OAuthCallback />;
    }
    return <LandingPage />;
  }

  // Define if we should allow redirection to LP (only on production, not on test/local)
  const isRedirectAllowed = !isLocalhost && !isTestDomain && !isLpDomain;

  return (
    <Routes>
      {/* 🧑‍💼 用户端页面 */}
      <Route path="/lp" element={<LandingPage />} />
      <Route path="/liff" element={<LiffEntry />} />
      <Route path="/pay/magic" element={<MagicPayment />} />
      <Route path="/invite" element={<InviteRedirect />} />
      {/* 法人専用リンク: 会社コードを URL で確定し、ログイン後に社員番号のみ入力して在籍確認 */}
      <Route path="/corp/:code" element={<CorpLanding />} />
      {/* 短縮リンク: /c/:linkCode → /api/c/:linkCode → FastAPI → LP にリダイレクト */}
      <Route path="/c/:linkCode" element={<CouponLinkPassthrough />} />
      <Route path="/consultation" element={<ConsultationPage />} />
      <Route path="/start" element={<LoginHandler />} />

      {/* /zoom-room.html は public/ 配下の静的 HTML（React Router 対象外） */}
      {/* 問診ルーム（全画面、レイアウトなし） */}
      <Route path="/consultation/:reservationId" element={<ProtectedRoute role="user"><ConsultationRoom /></ProtectedRoute>} />
      {/* 医師向け問診ページ（全画面、DoctorLayout なし） */}
      <Route path="/doctor/consultation/:reservationId" element={<ProtectedRoute role="doctor"><DoctorConsultation /></ProtectedRoute>} />

      <Route element={<MainLayout ipAllowed={ipAllowed} />}>
        {/* TEMPORARY REDIRECT FOR TEST SERVER: Redirect main app entry to LP, but show LandingPage for test/local */}
        <Route path="/" element={isRedirectAllowed ? <ExternalRedirect to={`https://lp.smhc.ai/${window.location.search}`} /> : <LandingPage />} />
        
        {/* Auth routes redirected to LP temporarily since login flow is complex, skip for test/local */}
        <Route path="/login" element={isRedirectAllowed ? <ExternalRedirect to="https://lp.smhc.ai" /> : <LandingPage />} />
        <Route path="/login_success" element={isRedirectAllowed ? <ExternalRedirect to="https://lp.smhc.ai" /> : <OAuthCallback />} />
        <Route path="/register" element={isRedirectAllowed ? <ExternalRedirect to="https://lp.smhc.ai" /> : <Register />} />
        <Route path="/signup" element={isRedirectAllowed ? <ExternalRedirect to="https://lp.smhc.ai" /> : <Register />} />
        <Route path="/forgot" element={isRedirectAllowed ? <ExternalRedirect to="https://lp.smhc.ai" /> : <Navigate to="/forgot-password" />} />
        <Route path="/reset-password" element={isRedirectAllowed ? <ExternalRedirect to={`https://lp.smhc.ai/reset-password${window.location.search}`} /> : <ResetPassword />} />
        
        {/* Static pages kept accessible */}
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/faq" element={<FAQPage />}>
          <Route path="pickup/:pid" element={<FaqPickupModal />} />
        </Route>
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/legal" element={<LegalPage />} />
        <Route path="/security" element={<SecurityPolicyPage />} />
        <Route path="/terms" element={<TermsOfUsePage />} />
        
        {/* Menu pages kept accessible for LINE in-app viewing */}
        <Route path="/menu/aga-male" element={<AgaMalePage ipAllowed={ipAllowed} />} />
        <Route path="/menu/aga-female" element={<AgaFemalePage ipAllowed={ipAllowed} />} />
        <Route path="/menu/obesity-male" element={<ObesityMalePage ipAllowed={ipAllowed} />} />
        <Route path="/menu/obesity-female" element={<ObesityFemalePage ipAllowed={ipAllowed} />} />
        <Route path="/menu/ed-premature" element={<EdPrematurePage ipAllowed={ipAllowed} />} />
        <Route path="/menu/medical-skincare" element={<MedicalSkincarePage ipAllowed={ipAllowed} />} />
        <Route path="/menu/pill" element={<PillPage ipAllowed={ipAllowed} />} />
        <Route path="/menu/pill/low-dose" element={<PillLowDosePage ipAllowed={ipAllowed} />} />
        <Route path="/menu/pill/period-shift" element={<PillPeriodShiftPage ipAllowed={ipAllowed} />} />
        <Route path="/menu/pill/after-pill" element={<AfterPillPage ipAllowed={ipAllowed} />} />
        <Route path="/faq/diet-female" element={<FaqMedicalDietFemale ipAllowed={ipAllowed} />} />
        <Route path="/faq/diet-male" element={<FaqMedicalDietMale ipAllowed={ipAllowed} />} />
        <Route path="/faq/aga-male" element={<FaqAgaMale ipAllowed={ipAllowed} />} />
        <Route path="/faq/aga-female" element={<FaqAgaFemale ipAllowed={ipAllowed} />} />
        <Route path="/faq/ed" element={<FaqEdPremature ipAllowed={ipAllowed} />} />
        <Route path="/faq/pill-low-dose" element={<FaqPill ipAllowed={ipAllowed} />} />
        <Route path="/faq/medical-skincare" element={<FaqMedicalSkincare ipAllowed={ipAllowed} />} />

        <Route path="/verify" element={<Verify />} />
        <Route path="/verify-pending" element={<VerifyPending />} />

        {/* ✅ 用户登录后可访问的页面 */}
        <Route element={<ProtectedRoute role="user"><Outlet /></ProtectedRoute>}>
          <Route path="/reserve" element={<ReservationPage />} />
          <Route path="/welcome" element={<Welcome />} />
          <Route element={<MypageShell />}>
            <Route path="/mypage" element={<MyPage />} />
            <Route path="/mypage/records" element={<MypageRecords />} />
            <Route path="/mypage/extra" element={<MypageExtra />} />
            <Route path="/mypage/messages" element={<MypageMessages />} />
            <Route path="/mypage/points" element={<MypagePoints />} />
          </Route>
          <Route path="/mypage/regular" element={<MypageRegular />} />
          <Route path="/mypage/profile" element={<UserProfilePage />} />
          <Route path="/my/reservations" element={<MyReservations />} />
        </Route>

        {/* 🧑‍⚕️ 诊疗菜单和问诊票公开页面 */}

        <Route path="/questionnaire/aga-male" element={<QuestionnaireAGAMale />} />
        <Route path="/questionnaire/aga-female" element={<QuestionnaireAGAFemale />} />
        <Route path="/questionnaire/ed-premature" element={<QuestionnaireEDPremature />} />
        <Route path="/questionnaire/obesity-female" element={<QuestionnaireObesityFemale />} />
        <Route path="/questionnaire/obesity-male" element={<QuestionnaireObesityMale />} />
        <Route path="/questionnaire/pill" element={<QuestionnairePill />} />
        <Route path="/questionnaire/skincare" element={<QuestionnaireMedicalSkincare />} />
        
        {/* 不明なURLは本番ではLPへ、ローカルでは404ページを表示 */}
        <Route path="*" element={isRedirectAllowed ? <ExternalRedirect to="https://lp.smhc.ai" /> : <NotFound />} />
      </Route>

      {/* 🩺 医生端页面 */}
      <Route path="/login/doctor" element={<DoctorLogin />} />
      <Route path="/doctor" element={<ProtectedRoute role="doctor"><DoctorLayout /></ProtectedRoute>}>
        <Route path="dashboard" element={<DoctorDashboard />} />
        <Route path="mypage" element={<DoctorMyPage />} />
        <Route path="/doctor/reservations" element={<DoctorReservations />} />
        <Route path="schedule" element={<DoctorSchedule />} />
        <Route path="users" element={<DoctorUsers />} />
        <Route path="upload/:id" element={<UploadPrescription />} />
        <Route path="questionnaire/:id" element={<DoctorQuestionnaire />} />
        <Route path="record/:id" element={<DoctorRecord />} />
        <Route path="*" element={<NotFound />} />
      </Route>

      {/* 👨‍💼 管理员端页面 */}
      <Route path="/login/admin" element={<AdminLogin />} />
      <Route path="/admin" element={<ProtectedRoute role="admin"><AdminLayout /></ProtectedRoute>}>
        <Route index element={<AdminDashboard />} /> {/* ✅ 关键：设置 index 路由 */}
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="medicines" element={<AdminMedicine />} />
        <Route path="medicines/list" element={<AdminMedicineList />} />
        <Route path="reservations" element={<AdminReservations />} />
        <Route path="ip-check" element={<AdminIPCheck />} />
        <Route path="doctors" element={<AdminDoctorList />} />
        <Route path="users" element={<AdminUserList />} />
        <Route path="/admin/schedules" element={<AdminSchedules />} />
        <Route path="/admin/schedules/doctor" element={<AdminDoctorView />} />
        <Route path="/admin/coupons" element={<AdminCouponList />} />
        <Route path="/admin/coupons/new" element={<AdminCouponCreate />} />
        <Route path="/admin/coupons/:id" element={<AdminCouponDetail />} />
        <Route path="/admin/referrals" element={<AdminReferralsPage />} />
        <Route path="/admin/companies" element={<AdminCompanyList />} />
        <Route path="/admin/points" element={<AdminPointsManage />} />
        <Route path="/admin/auto-assign" element={<AdminAutoAssignPage />} />
        <Route path="/admin/sender-addresses" element={<AdminSenderAddresses />} />
        <Route path="delivery" element={<AdminDeliveryManagement />} />
        <Route path="refills" element={<AdminAdditionalSupply />} />
        <Route path="users/merge" element={<AdminUserMerge />} />
        <Route path="zoom/management" element={<AdminZoomManagement />} />
        <Route path="notification-settings" element={<AdminNotificationSettings />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
};

export default AppRoutes;
